import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  EmojiEvents,
  Visibility,
  School,
} from '@mui/icons-material';
import axios from 'axios';
import { motion } from 'framer-motion';
import Header from '../Components/Header';
import StudentSidebar from './StudentSdebar';

const GOLD = "#D4AF37";

const Certificates = () => {
  const theme = useTheme();
  const [displayedCertificates, setDisplayedCertificates] = useState(3);
  const [certificateData, setCertificateData] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    const fetchCertificates = async () => {
      setLoading(true);
      // In the original code, it was hardcoded to ID 22 for some reason. 
      // Keeping original behavior but polishing UI.
      const certificateId = 22;
      try {
        const response = await axios.post(
          `https://tdtlworld.com/dss-kaas-backend/lms/generate-certificate/${certificateId}/`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob', 
          }
        );

        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const newCertificate = {
          id: certificateId,
          url: pdfUrl,
          title: `Professional Excellence Certificate`,
          issuer: 'DB-Shiksha Academy',
          date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        };

        setCertificateData([newCertificate]);
      } catch (error) {
        console.error('Error fetching certificate:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchCertificates();
  }, [token]);

  const handleViewCertificate = (certificate) => {
    window.open(certificate.url, '_blank');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: '#fcfcfc' }}>
      <Header />
      
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4, lg: 6 },
            width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ mb: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box>
                <Typography variant="h2" fontWeight={900} gutterBottom sx={{ 
                  letterSpacing: '-1px',
                  background: `linear-gradient(45deg, #1a1a1a, ${GOLD})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Achievements
                </Typography>
                <Typography variant="h6" color="text.secondary" fontWeight={400}>
                  Your verified certifications and academic milestones.
                </Typography>
              </Box>
              
              {certificateData.length > 3 && (
                <Button 
                  onClick={() => setDisplayedCertificates(displayedCertificates === 3 ? certificateData.length : 3)}
                  sx={{ fontWeight: 800, color: GOLD }}
                >
                  {displayedCertificates === 3 ? 'View All' : 'View Less'}
                </Button>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 15 }}>
                <CircularProgress size={60} thickness={4} sx={{ color: GOLD, mb: 3 }} />
                <Typography variant="h6" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 2 }}>
                  GENERATING PORTFOLIO...
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {certificateData.length > 0 ? (
                  certificateData.slice(0, displayedCertificates).map((cert, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <Card sx={{ 
                          borderRadius: '32px', 
                          border: '1px solid', 
                          borderColor: 'divider',
                          overflow: 'hidden',
                          transition: '0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                          '&:hover': { 
                            transform: 'translateY(-12px)', 
                            borderColor: GOLD,
                            boxShadow: `0 24px 48px ${alpha(GOLD, 0.15)}`
                          }
                        }}>
                          <Box sx={{ 
                            height: 200, 
                            bgcolor: alpha(GOLD, 0.02), 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            position: 'relative'
                          }}>
                            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                              <School sx={{ fontSize: '1.5rem', color: alpha(GOLD, 0.3) }} />
                            </Box>
                            <EmojiEvents sx={{ fontSize: '6rem', color: GOLD, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }} />
                          </Box>
                          <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" fontWeight={900} gutterBottom sx={{ lineHeight: 1.2 }}>{cert.title}</Typography>
                            <Stack spacing={0.5} sx={{ mb: 4 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                ISSUED BY: {cert.issuer}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                EARNED ON: {cert.date}
                              </Typography>
                            </Stack>
                            <Button 
                              fullWidth 
                              variant="contained" 
                              startIcon={<Visibility />}
                              onClick={() => handleViewCertificate(cert)}
                              sx={{ 
                                borderRadius: '50px', 
                                fontWeight: 800, 
                                py: 1.5,
                                bgcolor: GOLD,
                                '&:hover': { bgcolor: alpha(GOLD, 0.8) }
                              }}
                            >
                              Verify & View
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 12, textAlign: 'center', borderRadius: '40px', bgcolor: alpha(GOLD, 0.02), border: '2px dashed', borderColor: alpha(GOLD, 0.1) }}>
                      <School sx={{ fontSize: '6rem', color: alpha(GOLD, 0.1), mb: 3 }} />
                      <Typography variant="h4" fontWeight={900} gutterBottom>No Certificates Yet</Typography>
                      <Typography variant="h6" color="text.secondary" fontWeight={400}>
                        Complete your courses and pass final assessments to earn verified certificates here.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Certificates;
